import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, formatCOP, imageUrl, setAuthToken, getAuthToken } from './services/api';
import ClientesPage from './pages/ClientesPage';
import VendedoresPage from './pages/VendedoresPage';
import VisitasPage from './pages/VisitasPage';
import InventarioPage from './pages/InventarioPage';
import ClientesImportPage from './pages/ClientesImportPage';
import UsuariosPage from './pages/UsuariosPage';
import CategoriasPage from './pages/CategoriasPage';
import DashboardPage from './pages/DashboardPage';
import PedidosPage from './pages/PedidosPage';
import BrandLogo from './components/BrandLogo';
import { ProductOpcionesEditor } from './components/ProductOpcionesModal';
import { fileToCompressedDataUrl } from './utils/compressImage';
import useRealtimeSync from './hooks/useRealtimeSync';
import useInvalidate from './hooks/useInvalidate';
import { broadcastInvalidate } from './services/realtime';
import { getAppUrls } from './utils/appUrls';
import { MANICURE_CATEGORY } from './config/unasLineas';

const USER_KEY = 'oh_admin_user';

const REALTIME_DOMAINS = ['product', 'inventory', 'client', 'order', 'user', 'seller', 'visit'];

function loadUser() {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function LoginGate({ onUnlock }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [backendOk, setBackendOk] = useState(null);

  useEffect(() => {
    api
      .ping()
      .then(() => setBackendOk(true))
      .catch(() => setBackendOk(false));
  }, []);

  async function submit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.login(username, password);
      sessionStorage.setItem(USER_KEY, JSON.stringify(res.data?.usuario || null));
      onUnlock({ token: res.data.token, usuario: res.data?.usuario || null });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="pin-screen pin-screen-pc">
      <div className="pin-screen-glow" aria-hidden="true" />
      <div className="pin-shell">
        <aside className="pin-brand-pane">
          <BrandLogo variant="pin" />
          <p className="pin-kicker">¡Oh! Belleza</p>
          <h1>Administración</h1>
          <p className="pin-sub">Panel de control en escritorio — pedidos, clientes, inventario y equipo.</p>
        </aside>
        <div className="pin-card pin-card-pc">
          <span className="pin-role">Acceso Admin</span>
          <h2 className="pin-form-title">Iniciar sesión</h2>
          {backendOk === false ? (
            <p className="error backend-warn">
              No hay conexión con el servidor. Enciende el API y vuelve a intentar.
            </p>
          ) : null}
          <form className="pin-form" onSubmit={submit}>
            <label className="pin-label" htmlFor="admin-username">
              Usuario
            </label>
            <div className="pin-input-wrap">
              <input
                id="admin-username"
                className="pin-input-text"
                type="text"
                autoComplete="username"
                placeholder="usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
              />
            </div>
            <label className="pin-label" htmlFor="admin-password">
              Contraseña
            </label>
            <div className="pin-input-wrap">
              <input
                id="admin-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="pin-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPassword ? 'Ocultar' : 'Ver'}
              </button>
            </div>
            {error ? <p className="error">{error}</p> : null}
            <button type="submit" className="pin-submit" disabled={loading || !username.trim() || !password}>
              {loading ? 'Entrando…' : 'Entrar'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function flagOn(value, fallback = true) {
  if (value == null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'object' && value && Array.isArray(value.data)) return Number(value.data[0]) !== 0;
  const n = Number(value);
  if (Number.isNaN(n)) return fallback;
  return n !== 0;
}

function draftFromProduct(product, categorias = []) {
  const ids = idsDesdeProducto(product, categorias);
  return {
    nombre: product.nombre || '',
    precio_app: product.precio_app ?? product.precio ?? 0,
    activo: flagOn(product.activo, true),
    ...ids,
  };
}

/** Foto ampliada del producto: se abre al hacer clic en la miniatura. */
function ProductPhotoModal({ product, onClose, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [warn, setWarn] = useState('');
  const [localPreview, setLocalPreview] = useState(null);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    setLocalPreview(null);
    setErr('');
    setWarn('');
  }, [product?.id]);

  if (!product) return null;
  const img = localPreview || imageUrl(product.imagen);

  async function onPickFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/i.test(file.type)) {
      setErr('Usa PNG, JPG, WEBP o GIF');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErr('La imagen supera 10 MB');
      return;
    }
    setUploading(true);
    setErr('');
    setWarn('');
    try {
      const data_url = await fileToCompressedDataUrl(file);
      setLocalPreview(data_url);
      const res = await api.uploadProductoImagen(product.id, {
        data_url,
        original_name: file.name,
      });
      if (res.data?.warning) setWarn(res.data.warning);
      onUploaded?.({
        ...product,
        imagen: res.data?.imagen || product.imagen,
      });
    } catch (ex) {
      setLocalPreview(null);
      setErr(ex.message || 'No se pudo subir la foto');
    } finally {
      setUploading(false);
    }
  }

  async function onClearPhoto() {
    if (!window.confirm('¿Dejar este producto SIN foto?\n\nQuedará vacío hasta que subas otra. La tienda no lo mostrará sin imagen.')) return;
    setUploading(true);
    setErr('');
    try {
      await api.clearProductoImagen(product.id);
      onUploaded?.({ ...product, imagen: null });
    } catch (ex) {
      const msg = String(ex.message || '');
      if (/404|Cannot POST|Cannot DELETE|Falta la imagen/i.test(msg)) {
        setErr(
          'El API del servidor aún no tiene “quitar foto”. Sube ohbelleza_API_completo_quitar_foto_20260812.zip en api.ohbellezaoficial.com (carpeta backend), reinicia Node en cPanel y verifica /api/ping → build 2026-08-12-admin-foto-clear.'
        );
      } else {
        setErr(msg || 'No se pudo quitar la foto');
      }
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="photo-modal-backdrop" onClick={onClose} role="presentation">
      <div className="photo-modal" onClick={(e) => e.stopPropagation()}>
        <header className="photo-modal-head">
          <div>
            <strong className="mono">{product.codigo}</strong>
            <span>{product.nombre}</span>
          </div>
          <button type="button" className="photo-modal-close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </header>
        {img ? (
          <img src={img} alt={product.nombre || product.codigo} className="photo-modal-img" key={img} />
        ) : (
          <p className="muted photo-modal-empty">Este producto no tiene foto.</p>
        )}
        <div className="photo-modal-actions">
          <button
            type="button"
            className="btn-primary"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? 'Subiendo…' : img ? 'Cambiar foto' : 'Subir foto (PNG/JPG)'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            hidden
            onChange={onPickFile}
          />
          {img ? (
            <button
              type="button"
              className="btn-secondary danger photo-modal-clear"
              disabled={uploading}
              onClick={onClearPhoto}
            >
              {uploading ? 'Quitando…' : 'Dejar sin foto'}
            </button>
          ) : null}
          {img ? (
            <a href={img} target="_blank" rel="noreferrer" className="photo-modal-link">
              Abrir imagen en pestaña nueva
            </a>
          ) : null}
        </div>
        {!img ? (
          <p className="muted photo-modal-empty-hint">Este producto puede quedarse sin foto. La tienda no lo mostrará hasta que subas una.</p>
        ) : null}
        {warn ? <p className="photo-modal-warn">{warn}</p> : null}
        {err ? <p className="error">{err}</p> : null}
      </div>
    </div>
  );
}

function findPadre(categorias, id) {
  const sid = String(id || '');
  if (!sid) return null;
  for (const c of categorias || []) {
    if (String(c.id) === sid) return c;
    if ((c.hijas || []).some((h) => String(h.id) === sid)) return c;
  }
  return null;
}

function hijasDe(categorias, padreId) {
  const p = (categorias || []).find((c) => String(c.id) === String(padreId || ''));
  return Array.isArray(p?.hijas) ? p.hijas : [];
}

function idsDesdeProducto(product, categorias = []) {
  const sid =
    product.subcategoria_id != null && product.subcategoria_id !== ''
      ? String(product.subcategoria_id)
      : '';
  const padre = findPadre(categorias, sid) || findPadre(categorias, product.categoria_id);
  let padreId = padre ? String(padre.id) : '';
  if (!padreId && product.categoria) {
    const byName = (categorias || []).find(
      (c) => c.nombre === product.categoria || c.nombre === 'Cabello' && product.categoria === 'Capilar'
    );
    if (byName) padreId = String(byName.id);
  }
  const esHija = Boolean(padre && sid && String(padre.id) !== sid);
  return {
    categoria_id: padreId,
    hija_id: esHija ? sid : '',
    subcategoria_id: sid || padreId,
    subcategoria: esHija
      ? findCategoriaNombre(categorias, sid) || product.subcategoria || ''
      : '',
  };
}

const CATEGORIA_ETIQUETA = {
  ASESORIOS: 'Accesorios',
  'ASESORIOS CABELLO': 'Accesorios cabello',
  BARBERIA: 'Barbería',
  'BRILLOS Y SOMBRAS': 'Brillos y sombras',
  'BROCHAS Y PINCELES': 'Brochas y pinceles',
  COSMETIQUERAS: 'Cosmetiqueras',
  ELECTRICOS: 'Eléctricos',
  ESMALTES: 'Esmaltes',
  'FACIAL Y CORPORAL': 'Facial y corporal',
  'PEINILLAS Y CEPILLOS': 'Peinillas y cepillos',
  PESTAÑAS: 'Pestañas',
  'STICKER FACIAL': 'Sticker facial',
  UÑAS: 'Uñas',
};

function categoriaVista(cat) {
  if (!cat) return '';
  const nom = cat.nombre || '';
  return cat.label || CATEGORIA_ETIQUETA[nom] || nom;
}

function CategoriaSelectOptions({ categorias }) {
  return (
    <>
      <option value="">— Elegir —</option>
      {(categorias || []).map((c) => (
        <option key={c.id || c.nombre} value={String(c.id)}>
          {categoriaVista(c)}
        </option>
      ))}
    </>
  );
}

function findCategoriaNombre(categorias, id) {
  const sid = String(id || '');
  if (!sid) return '';
  for (const c of categorias || []) {
    if (String(c.id) === sid) return c.nombre;
    const hit = (c.hijas || []).find((h) => String(h.id) === sid);
    if (hit) return hit.nombre;
  }
  return '';
}

function ProductRow({ product, pin, onSaved, onDeleted, categorias }) {
  const [draft, setDraft] = useState(() => draftFromProduct(product, categorias));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [opsOpen, setOpsOpen] = useState(false);
  const productId = product.id;

  // Solo al cambiar de producto (no al actualizar el objeto padre: eso re-marcaba WEB/APP)
  useEffect(() => {
    setDraft(draftFromProduct(product, categorias));
    setError('');
    setOkMsg('');
  }, [productId]); // product se lee al cambiar de id a propósito

  useEffect(() => {
    if (!categorias?.length) return;
    setDraft((d) => {
      if (d.categoria_id && findPadre(categorias, d.categoria_id)) return d;
      return { ...d, ...idsDesdeProducto(product, categorias) };
    });
  }, [categorias, productId]);

  async function save(nextDraft = draft, revertDraft = null) {
    setSaving(true);
    setError('');
    setOkMsg('');
    const precio = Number(nextDraft.precio_app) || 0;
    const payload = {
      nombre: String(nextDraft.nombre || '').trim(),
      precio_app: precio,
      precio_web: precio,
      activo: nextDraft.activo ? 1 : 0,
      visible_app: 1,
      visible_web: 1,
    };
    if (nextDraft.categoria_id || nextDraft.hija_id) {
      const assignId = nextDraft.hija_id || nextDraft.categoria_id;
      payload.subcategoria_id = Number(assignId);
      payload.subcategoria = findCategoriaNombre(categorias, assignId) || nextDraft.subcategoria || '';
    }
    try {
      const saved = await api.updateProducto(product.id, payload);
      const d = saved?.data;
      if (!d || d.visible_app === undefined || d.visible_web === undefined) {
        throw new Error('El servidor no guardó los canales. Reinicia el API en cPanel (Node Application → Restart) y recarga Admin.');
      }
      const serverApp = flagOn(d.visible_app, false);
      const serverWeb = flagOn(d.visible_web, false);
      if (serverApp !== !!payload.visible_app || serverWeb !== !!payload.visible_web) {
        throw new Error(
          `No se guardó el canal (APP=${payload.visible_app} WEB=${payload.visible_web} → servidor APP=${serverApp ? 1 : 0} WEB=${serverWeb ? 1 : 0}). Reinicia el backend.`
        );
      }
      // Confiar en lo enviado; no dejar que un merge del padre revierta
      const applied = {
        nombre: d.nombre ?? nextDraft.nombre,
        precio_app: d.precio_app ?? nextDraft.precio_app,
        activo: flagOn(d.activo, nextDraft.activo),
        subcategoria: d.subcategoria || nextDraft.subcategoria || '',
        subcategoria_id:
          d.subcategoria_id != null && d.subcategoria_id !== ''
            ? String(d.subcategoria_id)
            : nextDraft.hija_id || nextDraft.categoria_id || '',
        categoria_id: nextDraft.categoria_id || '',
        hija_id: nextDraft.hija_id || '',
      };
      setDraft(applied);
      setOkMsg('Guardado');
      onSaved?.({
        ...product,
        ...d,
        activo: applied.activo ? 1 : 0,
        visible_app: 1,
        visible_web: 1,
        subcategoria: applied.subcategoria,
        subcategoria_id: applied.subcategoria_id,
        categoria_id: applied.categoria_id,
        categoria: findCategoriaNombre(categorias, applied.categoria_id) || product.categoria,
      });
      setTimeout(() => setOkMsg(''), 1800);
    } catch (err) {
      setError(err.message || 'No se pudo guardar');
      // Si falló un toggle, volver al estado anterior (no dejar WEB/APP “falso”)
      if (revertDraft) setDraft(revertDraft);
    } finally {
      setSaving(false);
    }
  }

  async function eliminarProducto() {
    const nombre = String(product.nombre || draft.nombre || '').trim() || 'este producto';
    const codigo = String(product.codigo || '').trim();
    const ok = window.confirm(
      `¿Eliminar "${nombre}"${codigo ? ` (${codigo})` : ''} del catálogo?\n\n` +
        'Desaparece de la tienda y la app. La foto en el servidor no se borra. ' +
        'Los pedidos antiguos conservan el ítem.'
    );
    if (!ok) return;
    setDeleting(true);
    setError('');
    setOkMsg('');
    try {
      await api.deleteProducto(product.id);
      broadcastInvalidate({
        domains: ['product', 'inventory'],
        reason: 'producto_eliminado',
        codigo,
      });
      onDeleted?.(product.id);
    } catch (err) {
      setError(err.message || 'No se pudo eliminar');
    } finally {
      setDeleting(false);
    }
  }

  const busy = saving || deleting;
  const rowOff = !draft.activo;

  const img = imageUrl(product.imagen);
  const opsList = Array.isArray(product.opciones) ? product.opciones : [];
  const nOps = opsList.length;
  const opsNombres = opsList.map((o) => String(o.nombre || '').trim()).filter(Boolean);

  return (
    <>
    <tr className={`product-row${rowOff ? ' product-row--dimmed' : ''}${opsOpen ? ' product-row--ops-open' : ''}`}>
      <td className="product-thumb-cell" data-label="Foto">
        <div className="product-foto-stack">
          {img ? (
            <button
              type="button"
              className="product-thumb-btn"
              onClick={() => setOpsOpen((v) => !v)}
              title="Fotos y sabores"
            >
              <img src={img} alt="" className="product-thumb" loading="lazy" />
              {nOps > 1 ? <span className="product-thumb-ops">{nOps}</span> : null}
            </button>
          ) : (
            <button
              type="button"
              className="product-thumb product-thumb--empty product-thumb--upload"
              onClick={() => setOpsOpen((v) => !v)}
              title="Fotos y sabores"
            >
              +
            </button>
          )}
          <button
            type="button"
            className={`product-ops-btn${opsOpen ? ' is-open' : ''}`}
            onClick={() => setOpsOpen((v) => !v)}
          >
            {opsOpen ? 'Cerrar' : nOps > 1 ? `${nOps} fotos / sabores` : 'Fotos y sabores'}
          </button>
          {opsNombres.length ? (
            <p className="product-ops-chips" title={opsNombres.join(' · ')}>
              {opsNombres.slice(0, 4).join(' · ')}
              {opsNombres.length > 4 ? '…' : ''}
            </p>
          ) : null}
        </div>
      </td>
      <td className="mono" data-label="Código">{product.codigo}</td>
      <td data-label="Nombre">
        <input value={draft.nombre} onChange={(e) => setDraft((d) => ({ ...d, nombre: e.target.value }))} />
      </td>
      <td data-label="" className="product-cats-cell">
        <label>
          Categoría
          <select
            className="product-cat-select"
            value={draft.categoria_id || ''}
            disabled={busy}
            onChange={(e) => {
              const prev = draft;
              const id = e.target.value;
              const next = {
                ...draft,
                categoria_id: id,
                hija_id: '',
                subcategoria_id: id,
                subcategoria: '',
              };
              setDraft(next);
              if (id) save(next, prev);
            }}
          >
            <option value="">— Elegir —</option>
            {(categorias || []).map((c) => (
              <option key={c.id || c.nombre} value={String(c.id)}>
                {categoriaVista(c)}
              </option>
            ))}
          </select>
        </label>
        {hijasDe(categorias, draft.categoria_id).length ? (
        <label>
          Subcategoría
          <select
            className="product-cat-select"
            value={draft.hija_id || ''}
            disabled={busy || !draft.categoria_id}
            onChange={(e) => {
              const prev = draft;
              const id = e.target.value;
              const next = {
                ...draft,
                hija_id: id,
                subcategoria_id: id || draft.categoria_id,
                subcategoria: id ? findCategoriaNombre(categorias, id) : '',
              };
              setDraft(next);
              save(next, prev);
            }}
          >
            <option value="">— Elegir subcategoría —</option>
            {hijasDe(categorias, draft.categoria_id).map((h) => (
              <option key={h.id} value={String(h.id)}>
                {categoriaVista(h)}
              </option>
            ))}
          </select>
        </label>
        ) : null}
      </td>
      <td data-label="Precio">
        <input
          type="number"
          className="num precio-app"
          value={draft.precio_app}
          onChange={(e) => setDraft((d) => ({ ...d, precio_app: e.target.value }))}
        />
        <small className="precio-hint">{formatCOP(draft.precio_app)}</small>
      </td>
      <td data-label="Cantidad" className="product-qty-cell">
        <strong className={`product-qty${Number(product.cantidad) <= 0 ? ' product-qty--zero' : ''}`}>
          {Number(product.cantidad) || 0}
        </strong>
        <small className="precio-hint">
          {flagOn(product.en_stock, Number(product.cantidad) > 0) ? 'En stock' : 'Agotado'}
        </small>
      </td>
      <td data-label="Estado">
        <button
          type="button"
          className={`estado-toggle${draft.activo ? ' on' : ''}`}
          disabled={busy}
          onClick={() => {
            const prev = draft;
            const next = { ...draft, activo: !draft.activo };
            setDraft(next);
            save(next, prev);
          }}
        >
          {draft.activo ? 'Activo' : 'Inactivo'}
        </button>
      </td>
      <td data-label="" className="product-row-actions">
        <button type="button" className="btn-save" disabled={busy} onClick={() => save()}>
          {saving ? '...' : 'Guardar'}
        </button>
        <button
          type="button"
          className="btn-secondary danger product-delete-btn"
          disabled={busy}
          onClick={eliminarProducto}
          title="Quitar del catálogo"
        >
          {deleting ? '…' : 'Eliminar'}
        </button>
        {okMsg ? <p className="ok-msg">{okMsg}</p> : null}
        {error ? <p className="error row-error">{error}</p> : null}
      </td>
    </tr>
    {opsOpen ? (
      <tr className="product-ops-row">
        <td colSpan={10} data-label="">
          <ProductOpcionesEditor
            product={product}
            onUploaded={(updated) => onSaved?.(updated)}
          />
        </td>
      </tr>
    ) : null}
    </>
  );
}

/** Formulario para crear un producto nuevo desde Admin. */
function CreateProductModal({ categorias, onClose, onCreated }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    codigo: '',
    nombre: '',
    referencia: '',
    descripcion: '',
    subcategoria: '',
    subcategoria_id: '',
    categoria_id: '',
    hija_id: '',
    precio_app: '',
    cantidad: '0',
    en_stock: true,
    activo: true,
  });
  const [preview, setPreview] = useState(null);
  const [dataUrl, setDataUrl] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [warn, setWarn] = useState('');

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onPickFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/i.test(file.type)) {
      setError('Usa PNG, JPG, WEBP o GIF');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('La imagen supera 10 MB');
      return;
    }
    setError('');
    const url = await fileToCompressedDataUrl(file);
    setDataUrl(url);
    setPreview(url);
    setOriginalName(file.name || '');
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        codigo: String(form.codigo || '').trim(),
        nombre: String(form.nombre || '').trim(),
        referencia: String(form.referencia || '').trim() || null,
        descripcion: String(form.descripcion || '').trim() || null,
        subcategoria: form.hija_id
          ? findCategoriaNombre(categorias, form.hija_id)
          : findCategoriaNombre(categorias, form.categoria_id) || null,
        subcategoria_id: form.hija_id || form.categoria_id
          ? Number(form.hija_id || form.categoria_id)
          : null,
        precio_app: Number(form.precio_app) || 0,
        precio_web: Number(form.precio_app) || 0,
        cantidad: Number(form.cantidad) || 0,
        en_stock: form.en_stock ? 1 : 0,
        activo: form.activo ? 1 : 0,
        visible_app: 1,
        visible_web: 1,
      };
      if (dataUrl) {
        payload.data_url = dataUrl;
        payload.original_name = originalName;
      }
      const res = await api.createProducto(payload);
      if (res.data?.warning) setWarn(res.data.warning);
      onCreated?.(res.data);
    } catch (err) {
      setError(err.message || 'No se pudo crear el producto');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="photo-modal-backdrop" onClick={onClose} role="presentation">
      <div className="create-product-modal" onClick={(e) => e.stopPropagation()}>
        <header className="photo-modal-head">
          <div>
            <strong>Nuevo producto</strong>
            <span>Código, precio, categoría, stock y foto</span>
          </div>
          <button type="button" className="photo-modal-close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </header>
        <form className="create-product-form" onSubmit={onSubmit}>
          <div className="create-product-grid">
            <label>
              Código <em>*</em>
              <input
                required
                value={form.codigo}
                onChange={(e) => setField('codigo', e.target.value)}
                placeholder="Ej: 230910"
              />
            </label>
            <label>
              Categoría
              <select
                value={form.categoria_id}
                onChange={(e) => {
                  const id = e.target.value;
                  setForm((f) => ({
                    ...f,
                    categoria_id: id,
                    hija_id: '',
                    subcategoria_id: id,
                    subcategoria: '',
                  }));
                }}
              >
                <CategoriaSelectOptions categorias={categorias} />
              </select>
            </label>
            {hijasDe(categorias, form.categoria_id).length ? (
            <label>
              Subcategoría
              <select
                value={form.hija_id}
                disabled={!form.categoria_id}
                onChange={(e) => {
                  const id = e.target.value;
                  setForm((f) => ({
                    ...f,
                    hija_id: id,
                    subcategoria_id: id || f.categoria_id,
                    subcategoria: id ? findCategoriaNombre(categorias, id) : '',
                  }));
                }}
              >
                <option value="">— Elegir subcategoría —</option>
                {hijasDe(categorias, form.categoria_id).map((h) => (
                  <option key={h.id} value={String(h.id)}>
                    {categoriaVista(h)}
                  </option>
                ))}
              </select>
            </label>
            ) : null}
            <label className="create-product-span2">
              Nombre <em>*</em>
              <input
                required
                value={form.nombre}
                onChange={(e) => setField('nombre', e.target.value)}
                placeholder="Nombre del producto"
              />
            </label>
            <label>
              Referencia
              <input
                value={form.referencia}
                onChange={(e) => setField('referencia', e.target.value)}
                placeholder="Opcional"
              />
            </label>
            <label>
              Cantidad
              <input
                type="number"
                min="0"
                value={form.cantidad}
                onChange={(e) => {
                  setField('cantidad', e.target.value);
                  const n = Number(e.target.value) || 0;
                  setField('en_stock', n > 0);
                }}
              />
            </label>
            <label>
              Precio
              <input
                type="number"
                min="0"
                value={form.precio_app}
                onChange={(e) => setField('precio_app', e.target.value)}
              />
            </label>
            <label className="create-product-span2">
              Descripción
              <textarea
                rows={3}
                value={form.descripcion}
                onChange={(e) => setField('descripcion', e.target.value)}
                placeholder="Opcional"
              />
            </label>
          </div>

          <div className="create-product-flags">
            <label>
              <input type="checkbox" checked={form.activo} onChange={(e) => setField('activo', e.target.checked)} />
              Activo
            </label>
            <label>
              <input
                type="checkbox"
                checked={form.en_stock}
                onChange={(e) => setField('en_stock', e.target.checked)}
              />
              En stock
            </label>
          </div>

          <div className="create-product-photo">
            {preview ? <img src={preview} alt="" className="create-product-preview" /> : null}
            <button type="button" className="btn-secondary" onClick={() => fileRef.current?.click()}>
              {preview ? 'Cambiar foto' : 'Adjuntar foto (PNG/JPG)'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              hidden
              onChange={onPickFile}
            />
          </div>

          {warn ? <p className="photo-modal-warn">{warn}</p> : null}
          {error ? <p className="error">{error}</p> : null}

          <div className="create-product-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Creando…' : 'Crear producto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function esCatManicure(nombre) {
  const n = String(nombre || '').trim();
  const norm = n.toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return norm === 'UNAS' || n === MANICURE_CATEGORY || n === 'Manicure y Pedicure';
}

function ProductsPage({ pin }) {
  const [q, setQ] = useState('');
  const [categoria, setCategoria] = useState('');
  const [foto, setFoto] = useState('');
  const [stock, setStock] = useState('');
  const [categorias, setCategorias] = useState([]);
  const [productos, setProductos] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [crearOpen, setCrearOpen] = useState(false);
  const limit = 50;

  const load = useCallback(async (opts) => {
    const silent = Boolean(opts && typeof opts === 'object' && opts.silent);
    const scrollEl = document.querySelector('.admin-main') || document.scrollingElement;
    const scrollY = silent ? (scrollEl?.scrollTop ?? window.scrollY ?? 0) : null;
    if (!silent) setLoading(true);
    try {
      const res = await api.getAdminProductos({ q, categoria, foto, stock, offset, limit });
      setProductos(res.data || []);
      setTotal(res.total || 0);
    } finally {
      if (!silent) setLoading(false);
      if (silent && scrollY != null) {
        const restore = () => {
          const el = document.querySelector('.admin-main') || document.scrollingElement;
          if (el && el !== document.scrollingElement) el.scrollTop = scrollY;
          else window.scrollTo(0, scrollY);
        };
        restore();
        requestAnimationFrame(restore);
      }
    }
  }, [pin, q, categoria, foto, stock, offset]);

  useEffect(() => {
    load();
  }, [load]);

  function loadCategorias() {
    return api
      .getAdminCategorias()
      .then((r) => {
        const list = (r.data || []).filter((c) => c.nombre && c.nombre !== 'Todas' && c.nombre !== 'Otros');
        setCategorias(list);
      })
      .catch(() =>
        api.getCategorias().then((r) => {
          const list = (r.data || [])
            .filter((c) => c.nombre && c.nombre !== 'Todas' && c.nombre !== 'Otros')
            .map((c) =>
              c.nombre === 'Capilar' || String(c.id) === 'Capilar'
                ? { ...c, id: c.id === 'Capilar' ? 6 : c.id, nombre: 'Cabello', label: 'Cabello' }
                : c
            );
          setCategorias(list);
        })
      )
      .catch(() => setCategorias([]));
  }

  useEffect(() => {
    loadCategorias();
  }, [pin]);

  useInvalidate(['product', 'inventory'], () => {
    load({ silent: true });
    loadCategorias();
  });
  const apps = getAppUrls();
  const padreActivo = categorias.find(
    (c) => c.nombre === categoria || (c.hijas || []).some((h) => h.nombre === categoria)
  );
  const hijasFiltro = padreActivo?.hijas || [];
  const esManicure = Boolean(padreActivo && esCatManicure(padreActivo.nombre));
  return (
    <div className="panel">
      <header className="panel-head">
        <h2>Productos y precios</h2>
        <div className="panel-head-actions">
          <span>{total} productos</span>
          <button type="button" className="btn-primary" onClick={() => setCrearOpen(true)}>
            + Nuevo producto
          </button>
        </div>
      </header>
      <p className="productos-ops-banner">
        En cada producto hay un botón <strong>Fotos y sabores</strong>: ahí agregas más fotos, sabores o presentaciones (cualquier categoría, no solo exfoliantes). El código sigue siendo el mismo.
      </p>
      <div className="toolbar productos-toolbar">
        <input
          type="search"
          className="productos-search"
          placeholder="Buscar código o nombre..."
          value={q}
          onChange={(e) => { setQ(e.target.value); setOffset(0); }}
        />
        <button type="button" onClick={() => load()}>Buscar</button>
      </div>
      <div className="pedidos-admin-chips productos-filter-chips" role="group" aria-label="Categoría">
        <button
          type="button"
          className={`chip-btn ${!categoria ? 'active' : ''}`}
          onClick={() => { setCategoria(''); setOffset(0); }}
        >
          Todas
        </button>
        {categorias.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`chip-btn ${padreActivo && String(padreActivo.id) === String(c.id) ? 'active' : ''}`}
            onClick={() => {
              const nombre = c.nombre === 'Capilar' ? 'Cabello' : c.nombre;
              setCategoria((prev) => {
                const yaEsta = padreActivo && String(padreActivo.id) === String(c.id);
                return yaEsta && prev === nombre ? '' : nombre;
              });
              setOffset(0);
            }}
          >
            {c.label || (c.nombre === 'Capilar' ? 'Cabello' : c.nombre)}
            {c.total != null ? ` · ${c.total}` : ''}
          </button>
        ))}
      </div>
      {padreActivo && !esManicure && hijasFiltro.length ? (
        <div className="admin-subcat-filter">
          <div className="admin-subcat-filter-head">
            {`Subcategoría · ${padreActivo.nombre}`}
          </div>
          <div className="admin-subcat-filter-scroll" role="group" aria-label="Subcategoría">
            <button
              type="button"
              className={`unas-linea-chip ${categoria === padreActivo.nombre ? 'active' : ''}`}
              onClick={() => {
                setCategoria(padreActivo.nombre);
                setOffset(0);
              }}
            >
              <strong>Todas</strong>
              {padreActivo.total != null ? <em>{padreActivo.total}</em> : null}
            </button>
            {hijasFiltro.map((h) => (
              <button
                key={h.id}
                type="button"
                className={`unas-linea-chip ${categoria === h.nombre ? 'active' : ''}`}
                onClick={() => {
                  setCategoria((prev) => (prev === h.nombre ? padreActivo.nombre : h.nombre));
                  setOffset(0);
                }}
              >
                <strong>{h.nombre}</strong>
                {h.total != null ? <em>{h.total}</em> : null}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <div className="pedidos-admin-chips productos-filter-chips" role="group" aria-label="Foto">
        <button
          type="button"
          className={`chip-btn ${!foto ? 'active' : ''}`}
          onClick={() => { setFoto(''); setOffset(0); }}
        >
          Todas las fotos
        </button>
        <button
          type="button"
          className={`chip-btn ${foto === 'con' ? 'active' : ''}`}
          onClick={() => { setFoto('con'); setOffset(0); }}
        >
          Con foto
        </button>
        <button
          type="button"
          className={`chip-btn ${foto === 'sin' ? 'active' : ''}`}
          onClick={() => { setFoto('sin'); setOffset(0); }}
        >
          Sin foto
        </button>
      </div>
      <div className="pedidos-admin-chips productos-filter-chips" role="group" aria-label="Stock">
        <button
          type="button"
          className={`chip-btn ${!stock ? 'active' : ''}`}
          onClick={() => { setStock(''); setOffset(0); }}
        >
          Todo stock
        </button>
        <button
          type="button"
          className={`chip-btn ${stock === 'si' ? 'active' : ''}`}
          onClick={() => { setStock('si'); setOffset(0); }}
        >
          En stock
        </button>
        <button
          type="button"
          className={`chip-btn ${stock === 'no' ? 'active' : ''}`}
          onClick={() => { setStock('no'); setOffset(0); }}
        >
          Agotado
        </button>
      </div>
      {loading && !productos.length ? <p className="muted">Cargando...</p> : (
        <div className="table-wrap">
          <table className="responsive-cards">
            <thead>
              <tr>
                <th className="product-thumb-th">Foto</th>
                <th>Código</th>
                <th>Nombre</th>
                <th>Categoría / subcategoría</th>
                <th>Precio</th>
                <th>Cantidad</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {productos.map((p) => (
                <ProductRow
                  key={p.id}
                  product={p}
                  pin={pin}
                  categorias={categorias}
                  onSaved={(updated) => {
                    if (updated) {
                      setProductos((list) =>
                        list.map((row) => (row.id === updated.id ? { ...row, ...updated } : row))
                      );
                    } else {
                      load();
                    }
                  }}
                  onDeleted={(id) => {
                    setProductos((list) => list.filter((row) => row.id !== id));
                    setTotal((t) => Math.max(0, t - 1));
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="pager">
        <button type="button" disabled={offset <= 0} onClick={() => setOffset((o) => Math.max(0, o - limit))}>← Anterior</button>
        <span>{offset + 1}–{Math.min(offset + limit, total)} de {total}</span>
        <button type="button" disabled={offset + limit >= total} onClick={() => setOffset((o) => o + limit)}>Siguiente →</button>
      </div>
      {crearOpen ? (
        <CreateProductModal
          categorias={categorias}
          onClose={() => setCrearOpen(false)}
          onCreated={(created) => {
            setCrearOpen(false);
            setOffset(0);
            if (created?.id) {
              setProductos((list) => [created, ...list.filter((p) => p.id !== created.id)]);
              setTotal((t) => t + 1);
            } else {
              load();
            }
          }}
        />
      ) : null}
    </div>
  );
}

const TAB_LABELS = {
  inicio: 'Inicio',
  pedidos: 'Pedidos',
  clientes: 'Clientes',
  productos: 'Productos',
  categorias: 'Categorías',
  vendedores: 'Vendedores',
  visitas: 'En campo',
  inventario: 'Inventario',
  'clientes-txt': 'Importar clientes',
  usuarios: 'Usuarios apps',
};

export default function App() {
  const [session, setSession] = useState(() => {
    const savedToken = getAuthToken();
    if (!savedToken) return null;
    setAuthToken(savedToken);
    return { token: savedToken, usuario: loadUser() };
  });
  const [tab, setTab] = useState('inicio');
  const [toast, setToast] = useState({ message: '', type: 'ok' });
  const [menuOpen, setMenuOpen] = useState(false);
  const [checkingSession, setCheckingSession] = useState(Boolean(getAuthToken()));

  function showToast(message, type = 'ok') {
    setToast({ message: String(message || ''), type: type === 'error' ? 'error' : 'ok' });
  }

  useEffect(() => {
    const saved = getAuthToken();
    if (!saved) {
      setCheckingSession(false);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      api.logout().catch(() => {});
      sessionStorage.removeItem(USER_KEY);
      setSession(null);
      setCheckingSession(false);
    }, 12000);
    setAuthToken(saved);
    api
      .me()
      .then((res) => {
        if (cancelled) return;
        sessionStorage.setItem(USER_KEY, JSON.stringify(res.data?.usuario || null));
        setSession({ token: saved, usuario: res.data?.usuario || null });
      })
      .catch(() => {
        if (cancelled) return;
        api.logout().catch(() => {});
        sessionStorage.removeItem(USER_KEY);
        setSession(null);
      })
      .finally(() => {
        if (cancelled) return;
        clearTimeout(timer);
        setCheckingSession(false);
      });
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!toast.message) return undefined;
    const t = setTimeout(() => setToast({ message: '', type: 'ok' }), 2800);
    return () => clearTimeout(t);
  }, [toast.message]);

  useEffect(() => {
    setMenuOpen(false);
  }, [tab]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('admin-menu-lock');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('admin-menu-lock');
    };
  }, [menuOpen]);

  const token = session?.token || '';

  const getToken = useCallback(() => getAuthToken(), []);
  // Al reconectar (o volver a la pestaña) se recargan todas las páginas abiertas.
  const reloadAll = useCallback(() => {
    try {
      window.dispatchEvent(
        new CustomEvent('oh:invalidate', {
          detail: { domains: REALTIME_DOMAINS, app: 'admin' },
        })
      );
    } catch {
      /* ignore */
    }
  }, []);
  useRealtimeSync({
    app: 'admin',
    enabled: Boolean(token) && !checkingSession,
    getToken,
    onReconnect: reloadAll,
  });

  function logout() {
    api.logout().catch(() => {});
    sessionStorage.removeItem(USER_KEY);
    setSession(null);
    setMenuOpen(false);
  }

  if (checkingSession) {
    return (
      <div className="pin-screen">
        <p className="muted">Verificando sesión...</p>
      </div>
    );
  }

  if (!session?.token) return <LoginGate onUnlock={setSession} />;

  function go(next) {
    setTab(next);
    setMenuOpen(false);
  }

  return (
    <div className={`admin-app${menuOpen ? ' menu-open' : ''}`}>
      <header className="admin-topbar">
        <button
          type="button"
          className="menu-toggle"
          aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span />
          <span />
          <span />
        </button>
        <div className="admin-topbar-brand">
          <strong>¡Oh! Belleza</strong>
          <span>{TAB_LABELS[tab] || 'Admin'}</span>
        </div>
      </header>

      <button
        type="button"
        className="admin-backdrop"
        aria-label="Cerrar menú"
        tabIndex={menuOpen ? 0 : -1}
        onClick={() => setMenuOpen(false)}
      />

      <aside className="admin-sidebar">
        <BrandLogo variant="sidebar" />
        <p className="sidebar-tag">Administración</p>
        {session.usuario?.nombre ? (
          <p className="sidebar-user">{session.usuario.nombre}</p>
        ) : null}
        <nav>
          {Object.entries(TAB_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={tab === key ? 'active' : ''}
              onClick={() => go(key)}
            >
              {label}
            </button>
          ))}
        </nav>
        <button type="button" className="logout" onClick={logout}>
          Cerrar sesión
        </button>
      </aside>
      <main className="admin-main">
        {tab === 'inicio' ? <DashboardPage pin={token} onNavigate={go} /> : null}
        {tab === 'pedidos' ? <PedidosPage pin={token} /> : null}
        {tab === 'clientes' ? <ClientesPage pin={token} onToast={showToast} /> : null}
        {tab === 'productos' ? <ProductsPage pin={token} /> : null}
        {tab === 'categorias' ? <CategoriasPage pin={token} onToast={showToast} /> : null}
        {tab === 'vendedores' ? <VendedoresPage pin={token} onToast={showToast} /> : null}
        {tab === 'visitas' ? <VisitasPage pin={token} onToast={showToast} /> : null}
        {tab === 'inventario' ? <InventarioPage pin={token} onToast={showToast} /> : null}
        {tab === 'clientes-txt' ? <ClientesImportPage pin={token} onToast={showToast} /> : null}
        {tab === 'usuarios' ? <UsuariosPage pin={token} onToast={showToast} /> : null}
      </main>
      {toast.message ? (
        <div className={`toast toast-${toast.type === 'error' ? 'error' : 'ok'}`}>{toast.message}</div>
      ) : null}
    </div>
  );
}
